import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import type { SubscriptionState } from '../admin/plans.types';
import { SupabaseService } from '../auth/supabase.service';
import { planExpiryFromEndDate } from './plan-expiry.util';

/** A CRM plan as shown on the "no plan" gate screen (read-only catalog entry). */
export interface AvailablePlan {
  readonly id: number;
  readonly tier_name: string;
  readonly price: number;
  readonly billing_period: string;
  readonly modules: string[];
}

/** How often an open CRM tab re-checks the plan, so a renewal or a lapse shows without signing in again. */
const REFRESH_MS = 15 * 60_000;

interface MyPlanPayload {
  readonly subscription?: { readonly status?: string; readonly end_date?: string | null };
  readonly plan?: { readonly modules?: string[] | null };
  /** From the shared lifecycle (public.plan_access_state); absent on databases before 2026-10-19. */
  readonly state?: SubscriptionState;
  readonly blocked?: boolean;
  readonly grace_until?: string | null;
}

/**
 * Tracks whether the signed-in user's clinic (home branch) may use the CRM.
 * Loaded at session start (AuthService) so route guards can read it
 * synchronously, then re-checked on window focus and every 15 minutes.
 *
 * Access is crm.get_my_plan().blocked — the same rule crm.my_permissions()
 * uses: trial, active and grace are in; no plan and cancelled are out; a plan
 * past its end date is out only once CRM enforcement is switched on (Super
 * admin › Plans). When access is lost the app moves to the /plans gate.
 */
@Injectable({ providedIn: 'root' })
export class PlanAccessService {
  private readonly supabase = inject(SupabaseService).client;
  private readonly router = inject(Router);

  private readonly _hasActivePlan = signal(false);
  readonly hasActivePlan = this._hasActivePlan.asReadonly();

  private readonly _state = signal<SubscriptionState | null>(null);
  /** none | trial | active | grace | expired | cancelled; null until loaded. */
  readonly state = this._state.asReadonly();

  /** The subscription's end_date, verbatim from the RPC. Null when open-ended or unknown. */
  private readonly _planEndDate = signal<string | null>(null);
  private readonly _graceUntil = signal<string | null>(null);
  /** The plan's module keys (CRM_PLAN_MODULES); null = not loaded, or a super admin (no plan). */
  private readonly _modules = signal<ReadonlySet<string> | null>(null);

  /**
   * How close this clinic's plan is to its end date, for the site-wide banner
   * (shared/plan-expiry-banner). Null when the plan has no end date — which is
   * not the same as "expires today" and must not render.
   */
  readonly planExpiry = computed(() => planExpiryFromEndDate(this._planEndDate()));

  /** While in grace: how long access lasts. Null otherwise. */
  readonly graceExpiry = computed(() => (this._state() === 'grace' ? planExpiryFromEndDate(this._graceUntil()) : null));

  private watching = false;

  async refresh(): Promise<void> {
    const had = this._hasActivePlan();
    try {
      // Via crm.get_my_plan(), not a plain table select: crm.clinic_subscriptions has a
      // second permissive RLS policy granting full-table read to anyone holding
      // admin.clinics.manage, so a plain select can return every clinic's rows for those
      // accounts (Postgres ORs permissive policies together). The RPC is SECURITY DEFINER
      // and filters explicitly by the caller's own branch.
      const { data, error } = await this.supabase.schema('crm').rpc('get_my_plan');
      const plan = (error ? null : data) as MyPlanPayload | null;
      const status = plan?.subscription?.status;
      const allowed =
        plan !== null && (typeof plan.blocked === 'boolean' ? !plan.blocked : status === 'trial' || status === 'active');
      this._hasActivePlan.set(allowed);
      this._state.set(plan ? (plan.state ?? null) : 'none');
      this._planEndDate.set(plan?.subscription?.end_date ?? null);
      this._graceUntil.set(plan?.grace_until ?? null);
      this._modules.set(plan?.plan?.modules ? new Set(plan.plan.modules) : null);
    } catch {
      this._hasActivePlan.set(false);
      this._state.set(null);
      this._planEndDate.set(null);
      this._graceUntil.set(null);
      this._modules.set(null);
    }
    this.followAccess(had);
    this.watch();
  }

  /** Leaves the app for the gate when access is lost mid-session, and back when it returns. */
  private followAccess(had: boolean): void {
    const now = this._hasActivePlan();
    if (had === now) return;
    const onGate = this.router.url.startsWith('/plans');
    if (!now && !onGate && had) void this.router.navigateByUrl('/plans');
    if (now && onGate) void this.router.navigateByUrl('/dashboard');
  }

  private watch(): void {
    if (this.watching || typeof window === 'undefined') return;
    this.watching = true;
    window.addEventListener('focus', () => void this.refreshIfSignedIn());
    setInterval(() => void this.refreshIfSignedIn(), REFRESH_MS);
  }

  private async refreshIfSignedIn(): Promise<void> {
    const { data } = await this.supabase.auth.getSession();
    if (data.session) await this.refresh();
  }

  /** All CRM plans for the gate screen — readable by any authenticated user (plans_read_authenticated). */
  async listAvailablePlans(): Promise<AvailablePlan[]> {
    const { data, error } = await this.supabase
      .schema('crm')
      .from('plans')
      .select('id, tier_name, price, billing_period, modules')
      .eq('is_default', true)
      .is('archived_at', null)
      .order('price', { ascending: true });

    if (error) {
      throw error;
    }

    return (data ?? []) as AvailablePlan[];
  }

  /**
   * Whether the clinic's plan includes a CRM module (a CRM_PLAN_MODULES key).
   * For choices about the clinic rather than the user — e.g. which steps the
   * workflow builder offers. Permission checks (PermissionsService.can) already
   * combine role and plan; use those for what a user may open or do.
   * True when no plan is loaded (super admins have none).
   */
  hasModule(key: string): boolean {
    const modules = this._modules();
    return modules === null || modules.has(key);
  }

  clear(): void {
    this._hasActivePlan.set(false);
    this._modules.set(null);
    this._state.set(null);
    this._planEndDate.set(null);
    this._graceUntil.set(null);
  }
}
